import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import DraggableModal from '../../components/ui/DraggableModal';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  
  const [serviceForm, setServiceForm] = useState({ name: '', description: '', image: '', base_price: '', category: 'portrait' });
  const [packageForm, setPackageForm] = useState({ name: '', tier: 'basic', price: '', features: '', description: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, packagesRes] = await Promise.all([
        adminFetch(`${API}/services`),
        adminFetch(`${API}/packages`)
      ]);
      setServices(await servicesRes.json());
      setPackages(await packagesRes.json());
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const openServiceModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        name: service.name,
        description: service.description,
        image: service.image,
        base_price: service.base_price,
        category: service.category
      });
    } else {
      setEditingService(null);
      setServiceForm({ name: '', description: '', image: '', base_price: '', category: 'portrait' });
    }
    setShowServiceModal(true);
  };

  const openPackageModal = (serviceId, pkg = null) => {
    setSelectedService(serviceId);
    if (pkg) {
      setEditingPackage(pkg);
      setPackageForm({
        name: pkg.name,
        tier: pkg.tier,
        price: pkg.price,
        features: pkg.features?.join('\n') || '',
        description: pkg.description
      });
    } else {
      setEditingPackage(null);
      setPackageForm({ name: '', tier: 'basic', price: '', features: '', description: '' });
    }
    setShowPackageModal(true);
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingService 
        ? `${API}/admin/services/${editingService.service_id}`
        : `${API}/admin/services`;
      
      const body = editingService 
        ? JSON.stringify({ ...serviceForm, base_price: parseFloat(serviceForm.base_price) })
        : new FormData();
      
      if (!editingService) {
        Object.entries(serviceForm).forEach(([key, value]) => body.append(key, value));
      }

      const response = await adminFetch(url, {
        method: editingService ? 'PUT' : 'POST',
        ...(editingService ? { headers: { 'Content-Type': 'application/json' }, body } : { body })
      });

      if (!response.ok) throw new Error('Error saving');
      toast.success(editingService ? 'Servicio actualizado' : 'Servicio creado');
      setShowServiceModal(false);
      fetchData();
    } catch (error) {
      toast.error('Error al guardar servicio');
    }
  };

  const handlePackageSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingPackage 
        ? `${API}/admin/packages/${editingPackage.package_id}`
        : `${API}/admin/packages`;
      
      const body = {
        service_id: selectedService,
        name: packageForm.name,
        tier: packageForm.tier,
        price: parseFloat(packageForm.price),
        features: packageForm.features.split('\n').filter(f => f.trim()),
        description: packageForm.description
      };

      const response = await adminFetch(url, {
        method: editingPackage ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Error saving');
      toast.success(editingPackage ? 'Paquete actualizado' : 'Paquete creado');
      setShowPackageModal(false);
      fetchData();
    } catch (error) {
      toast.error('Error al guardar paquete');
    }
  };

  const deleteService = async (serviceId) => {
    if (!confirm('¿Eliminar este servicio y todos sus paquetes?')) return;
    try {
      await adminFetch(`${API}/admin/services/${serviceId}`, { method: 'DELETE' });
      toast.success('Servicio eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const deletePackage = async (packageId) => {
    if (!confirm('¿Eliminar este paquete?')) return;
    try {
      await adminFetch(`${API}/admin/packages/${packageId}`, { method: 'DELETE' });
      toast.success('Paquete eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getPackagesForService = (serviceId) => packages.filter(p => p.service_id === serviceId);
  const tierLabels = { basic: 'Básico', standard: 'Estándar', premium: 'Premium' };

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl mb-2">Servicios y Tarifas</h1>
            <p className="text-muted-foreground">Gestiona tus servicios y paquetes</p>
          </div>
          <Button onClick={() => openServiceModal()} className="gap-2">
            <Plus className="w-4 h-4" /> Nuevo Servicio
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-muted animate-pulse" />)}</div>
        ) : (
          <div className="space-y-6">
            {services.map((service) => (
              <div key={service.service_id} className="bg-card border border-border">
                <div className="p-4 flex items-center gap-4 border-b border-border">
                  {service.image && (
                    <img src={service.image} alt={service.name} className="w-16 h-16 object-cover rounded" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-medium">{service.name}</h3>
                    <p className="text-sm text-muted-foreground">Desde {service.base_price}€</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openPackageModal(service.service_id)}>
                      <Package className="w-4 h-4 mr-2" /> Añadir Paquete
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openServiceModal(service)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteService(service.service_id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {getPackagesForService(service.service_id)
                      .sort((a, b) => ['basic', 'standard', 'premium'].indexOf(a.tier) - ['basic', 'standard', 'premium'].indexOf(b.tier))
                      .map((pkg) => (
                        <div key={pkg.package_id} className="border border-border p-4 relative group">
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                            <button onClick={() => openPackageModal(service.service_id, pkg)} className="p-1 hover:bg-muted rounded">
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button onClick={() => deletePackage(pkg.package_id)} className="p-1 hover:bg-muted rounded">
                              <Trash2 className="w-3 h-3 text-destructive" />
                            </button>
                          </div>
                          <span className="text-xs text-muted-foreground uppercase">{tierLabels[pkg.tier]}</span>
                          <h4 className="font-medium">{pkg.name}</h4>
                          <p className="font-serif text-2xl my-2">{pkg.price}€</p>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            {pkg.features?.slice(0, 3).map((f, i) => <li key={i}>• {f}</li>)}
                            {pkg.features?.length > 3 && <li>+{pkg.features.length - 3} más</li>}
                          </ul>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Service Modal */}
      {showServiceModal && (
        <DraggableModal onClose={() => setShowServiceModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[28rem] max-w-[calc(100vw-2rem)]">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">{editingService ? 'Editar Servicio' : 'Nuevo Servicio'}</h2>
              <button onClick={() => setShowServiceModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleServiceSubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Nombre</label>
                <Input value={serviceForm.name} onChange={e => setServiceForm({...serviceForm, name: e.target.value})} required />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Descripción</label>
                <Textarea value={serviceForm.description} onChange={e => setServiceForm({...serviceForm, description: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">URL de imagen</label>
                <Input value={serviceForm.image} onChange={e => setServiceForm({...serviceForm, image: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Precio base (€)</label>
                <Input type="number" value={serviceForm.base_price} onChange={e => setServiceForm({...serviceForm, base_price: e.target.value})} required />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowServiceModal(false)}>Cancelar</Button>
                <Button type="submit">Guardar</Button>
              </div>
            </form>
          </motion.div>
        </DraggableModal>
      )}

      {/* Package Modal */}
      {showPackageModal && (
        <DraggableModal onClose={() => setShowPackageModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[28rem] max-w-[calc(100vw-2rem)]">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">{editingPackage ? 'Editar Paquete' : 'Nuevo Paquete'}</h2>
              <button onClick={() => setShowPackageModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handlePackageSubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Nombre</label>
                <Input value={packageForm.name} onChange={e => setPackageForm({...packageForm, name: e.target.value})} required />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Nivel</label>
                <select 
                  value={packageForm.tier} 
                  onChange={e => setPackageForm({...packageForm, tier: e.target.value})}
                  className="w-full h-10 px-3 border border-input bg-background rounded-md"
                >
                  <option value="basic">Básico</option>
                  <option value="standard">Estándar</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Precio (€)</label>
                <Input type="number" value={packageForm.price} onChange={e => setPackageForm({...packageForm, price: e.target.value})} required />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Características (una por línea)</label>
                <Textarea 
                  value={packageForm.features} 
                  onChange={e => setPackageForm({...packageForm, features: e.target.value})} 
                  placeholder="1 hora de sesión&#10;20 fotos editadas&#10;Galería online"
                  rows={5}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Descripción</label>
                <Input value={packageForm.description} onChange={e => setPackageForm({...packageForm, description: e.target.value})} />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowPackageModal(false)}>Cancelar</Button>
                <Button type="submit">Guardar</Button>
              </div>
            </form>
          </motion.div>
        </DraggableModal>
      )}
    </div>
  );
};

export default AdminServices;
